/** @jsxImportSource @emotion/react */

import { forwardRef } from 'react';

import { useTheme } from '@emotion/react';
import { data, TarotCard } from '../Data/index';
import { Social } from '../Social/social';

type ResultProps = {
  resultDetails: TarotCard;
};

export const Result = forwardRef<HTMLDivElement, ResultProps>(({ resultDetails }, ref) => {
  const theme = useTheme();

  return (
    <section
      ref={ref}
      css={{
        marginBottom: '3rem',
        padding: '0 1.5rem'
      }}
    >
      <h2
        css={{
          fontFamily: theme.fontFamily.heading,
          fontSize: theme.fontSizes[6],
          fontWeight: theme.fontWeights.semibold,
          marginBottom: '1rem',
          textAlign: 'center',

          [`@media (min-width: 640px)`]: {
            fontSize: theme.fontSizes[10],
            marginBottom: '25px',
            textAlign: 'left'
          }
        }}
      >
        In store for 2021 is...
      </h2>
      <div
        css={{
          [`@media (min-width: 768px)`]: {
            display: 'flex',
            marginLeft: '-50px',
          }
        }}
      >
        <picture
          css={{
            display: 'block',
            margin: '0 auto',
            width: '300px',

            [`@media (min-width: 768px)`]: {
              width: '40%'
            }
          }}
        >
          <img
            alt={resultDetails.image.alt}
            css={{
              display: 'block',
              height: 'auto',
              width: '100%'
            }}
            loading="lazy"
            src={resultDetails.image.src}
          />
        </picture>
        <div
          css={{
            [`@media (min-width: 768px)`]: {
              width: '60%'
            }
          }}
        >
          <p
            css={{
              fontSize: theme.fontSizes[3],
              fontWeight: theme.fontWeights.bold,

              [`@media (min-width: 768px)`]: {
                fontSize: theme.fontSizes[6]
              }
            }}
          >
            {`${resultDetails.title}`}
          </p>
          <p>{resultDetails.description}</p>
          <Social resultDetails={resultDetails} />
        </div>
      </div>

      {resultDetails.habs && (
        <div
          css={{
            label: 'hab-container',
            margin: '2rem 0 2.5rem 0',

            [`@media (min-width: 768px)`]: {
              margin: '4rem 0 3rem 0',
            }
          }}
        >
          <h3
            css={{
              fontSize: theme.fontSizes[4],
              fontWeight: theme.fontWeights.bold
            }}
          >
            {resultDetails.habsHeading ?? 'Check out the other Habs who will be having a similar 2021:'}
          </h3>

          <ul
            css={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              listStyleType: 'none',
              margin: 0,
              padding: 0
            }}
          >
            {resultDetails.habs.map((h) => (
              <li key={h.email}>
                <a
                  href={h.link ? h.link : `https://www.habaneroconsulting.com/team/${h.name.toLowerCase().replace(/\s/g, '-')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    alt={h.name}
                    css={{
                      backgroundColor: '#161e21',
                      borderRadius: '50%',
                      display: 'block',
                      height: 52,
                      marginTop: '0.5rem',
                      marginRight: '0.5rem',
                      objectFit: 'cover',
                      objectPosition: 'top',
                      width: 52,

                      [`@media (min-width: 768px)`]: {
                        marginRight: '1.5rem'
                      }
                    }}
                    loading="lazy"
                    src={h.image.src}
                    width={52}
                  />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <p
          css={{
            color: theme.color.slate,
            display: 'inline',
            paddingRight: '0.5rem'
          }}
        >
          {data.dictionary.other_results}
        </p>
        <a
          css={{
            color: theme.color.slate,

            '&:hover': {
              color: theme.color.primary
            }
          }}
          href={data.dictionary.other_results_url}
          target="_blank"
          rel="noopener noreferrer"
        >{data.dictionary.other_results_link_text}</a>
      </div>
    </section>
  );
});

Result.displayName = 'Result';
